<template>
  <button 
    :class="[
      'app-button', 
      `app-button--${variant}`,
      `app-button--${size}`,
      { 'app-button--loading': loading },
      { 'app-button--block': block },
      { 'app-button--icon-only': iconOnly },
      { 'app-button--rounded': rounded },
      { 'app-button--elevated': elevated },
      { 'app-button--soft': soft }
    ]" 
    :disabled="disabled || loading"
    @click="handleClick">
    <span v-if="loading" class="app-button__loader"></span>
    <span v-if="iconLeft" class="app-button__icon app-button__icon--left">
      <slot name="icon-left"></slot>
    </span>
    <span v-if="!loading" class="app-button__content">
      <slot></slot>
    </span>
    <span v-if="iconRight" class="app-button__icon app-button__icon--right">
      <slot name="icon-right"></slot>
    </span>
    <span v-if="badge" class="app-button__badge">{{ badge }}</span>
  </button>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { computed } from 'vue'

// Define props interface
interface Props {
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark' | 'outline' | 'text'
  size?: 'small' | 'medium' | 'large'
  loading?: boolean
  disabled?: boolean
  block?: boolean
  iconLeft?: boolean
  iconRight?: boolean
  iconOnly?: boolean
  rounded?: boolean
  elevated?: boolean
  soft?: boolean
  badge?: string | number | null
}

// Define emits
interface Emits {
  click: [event: MouseEvent]
}

// Define props with defaults
const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'medium',
  loading: false,
  disabled: false,
  block: false,
  iconLeft: false,
  iconRight: false,
  iconOnly: false,
  rounded: false,
  elevated: false,
  soft: false,
  badge: null
})

// Define emits
const emit = defineEmits<Emits>()

// Computed properties for validation
const isValidVariant = computed(() => {
  const validVariants = ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark', 'outline', 'text']
  return validVariants.includes(props.variant)
})

const isValidSize = computed(() => {
  const validSizes = ['small', 'medium', 'large']
  return validSizes.includes(props.size)
})

// Methods
const handleClick = (event: MouseEvent): void => {
  if (!props.disabled && !props.loading) {
    emit('click', event)
  }
}
</script>

<style scoped>
/* Use design system app-button classes from construction-app.css */
.app-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  font-family: var(--font-family);
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-sm);
  line-height: 1.5;
  text-decoration: none;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast) ease;
  position: relative;
  overflow: hidden;
  outline: none;
  user-select: none;
  white-space: nowrap;
}

.app-button:focus-visible {
  box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.2);
}

.app-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

/* Button sizes using design system */
.app-button--small {
  padding: var(--space-2) var(--space-3);
  font-size: var(--font-size-xs);
  min-height: 32px;
}

.app-button--medium {
  padding: var(--space-3) var(--space-4);
  font-size: var(--font-size-sm);
  min-height: 40px;
}

.app-button--large {
  padding: var(--space-4) var(--space-6);
  font-size: var(--font-size-base);
  min-height: 48px;
}

/* Button variants using design system */
.app-button--primary {
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  color: white;
  box-shadow: var(--shadow-sm);
}

.app-button--primary:hover {
  background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%);
  box-shadow: var(--shadow-base);
  transform: translateY(-1px);
}

.app-button--secondary {
  background: linear-gradient(135deg, var(--secondary) 0%, var(--secondary-dark) 100%);
  color: white;
  box-shadow: var(--shadow-sm);
}

.app-button--secondary:hover {
  background: linear-gradient(135deg, var(--secondary-dark) 0%, var(--secondary) 100%);
  box-shadow: var(--shadow-base);
  transform: translateY(-1px);
}

.app-button--success {
  background-color: var(--success);
  color: white;
  box-shadow: var(--shadow-sm);
}

.app-button--success:hover {
  background-color: var(--success-dark);
  box-shadow: var(--shadow-base);
  transform: translateY(-1px);
}

.app-button--warning {
  background-color: var(--warning);
  color: var(--neutral-900);
  box-shadow: var(--shadow-sm);
}

.app-button--warning:hover {
  background-color: var(--warning-dark);
  box-shadow: var(--shadow-base);
  transform: translateY(-1px);
}

.app-button--danger {
  background-color: var(--error);
  color: white;
  box-shadow: var(--shadow-sm);
}

.app-button--danger:hover {
  background-color: var(--error-dark);
  box-shadow: var(--shadow-base);
  transform: translateY(-1px);
}

.app-button--info {
  background-color: var(--info);
  color: white;
  box-shadow: var(--shadow-sm);
}

.app-button--info:hover {
  background-color: var(--info-dark);
  box-shadow: var(--shadow-base);
  transform: translateY(-1px);
}

.app-button--light {
  background-color: var(--neutral-200);
  color: var(--neutral-700);
}

.app-button--light:hover {
  background-color: var(--neutral-300);
}

.app-button--dark {
  background-color: var(--neutral-800);
  color: white;
}

.app-button--dark:hover {
  background-color: var(--neutral-900);
}

.app-button--outline {
  background-color: transparent;
  border: 2px solid var(--neutral-300);
  color: var(--neutral-700);
}

.app-button--outline:hover {
  border-color: var(--primary);
  color: var(--primary);
  background-color: rgba(var(--primary-rgb), 0.05);
}

.app-button--text {
  background-color: transparent;
  color: var(--primary);
  padding-left: var(--space-2);
  padding-right: var(--space-2);
}

.app-button--text:hover {
  background-color: rgba(var(--primary-rgb), 0.1);
}

/* Button states */
.app-button--loading {
  pointer-events: none;
}

.app-button--block {
  width: 100%;
  justify-content: center;
}

.app-button--icon-only {
  aspect-ratio: 1;
  padding: var(--space-2);
}

.app-button--rounded {
  border-radius: var(--radius-full);
}

.app-button--elevated {
  box-shadow: var(--shadow-base);
}

.app-button--elevated:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-2px);
}

.app-button--soft {
  background-color: rgba(var(--primary-rgb), 0.1);
  color: var(--primary);
}

.app-button--soft:hover {
  background-color: rgba(var(--primary-rgb), 0.2);
}

/* Icons */
.app-button__icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.app-button__icon--left {
  margin-right: var(--space-2);
}

.app-button__icon--right {
  margin-left: var(--space-2);
}

/* Loading spinner using design system */
.app-button__loader {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: button-spin 0.8s linear infinite;
}

@keyframes button-spin {
  to { transform: rotate(360deg); }
}

/* Badge */
.app-button__badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: var(--error);
  color: white;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-full);
  height: 20px;
  width: 20px;
  padding: 1px;
}
</style>
